import { useRef, useEffect } from "react";
import type { JSX } from "react";
import type { UIMessage } from "ai";
import { ChatMessage } from "./ChatMessage";
import { ThinkingPulse } from "./ThinkingPulse";
import type { TurnPhase } from "../hooks/useChat";
import { EmptyState } from "./EmptyState";
import { AlertTriangle } from "lucide-react";
import { useI18n } from "../i18n";
import type { TranslationKey } from "../i18n/types";
import type { AudioCache } from "../voice/audio-cache";
import type { Reaction } from "../hooks/useReactions";
import type { MessageMetrics } from "../hooks/useChat";

interface MessageListProps {
  messages: UIMessage[];
  messageMetrics?: Map<string, MessageMetrics>;
  isLoading: boolean;
  error?: Error;
  /** PATH B SLICE 4A — the active session's companion display name, threaded
   *  down to each ChatMessage so the assistant bubble shows the right name +
   *  avatar. Falls back to ChatMessage's own "Rook" default when omitted. */
  companionName?: string;
  onSpeak?: (message: UIMessage) => void;
  speakingMessageId?: string | null;
  seekTo?: (messageId: string, fraction: number) => void;
  getPlaybackProgress?: () => number;
  audioCache?: AudioCache;
  voiceId?: string;
  playbackSpeed?: number;
  onSpeedChange?: (speed: number) => void;
  /** Callback that receives the scroll container ref for external scroll listeners (e.g. bar collapse). */
  onContainerRef?: (el: HTMLDivElement | null) => void;
  reactions?: Record<string, Reaction[]>;
  onReact?: (messageId: string, emoji: string) => void;
  isKeepsaked?: (messageId: string) => boolean;
  onKeepsake?: (messageId: string) => void;
  scrollToMessageId?: string | null;
  onScrollToComplete?: () => void;
  onSendMessage?: (text: string) => void;
  isReplaying?: boolean;
  /** When false, all reasoning/thinking blocks are hidden across all messages. */
  showReasoning?: boolean;
  turnPhase?: TurnPhase;
  turnStartedAt?: number | null;
  subprocessAlive?: boolean;
  /** Email Composer v2 — the active session's own id, threaded down to
   *  ChatMessage → ToolCardDispatch → EmailDraftCard so the composer's
   *  send/save/hydrate REST calls can name their target (session_id +
   *  tool_call_id — never an account slot or draft id). */
  sessionId?: string | null;
}

const ERROR_MESSAGE_MAX_LENGTH = 300;

// Errors can carry a raw provider/API message (e.g. "context_length_exceeded",
// org-policy rejections) — surface it instead of a generic string, but bound
// it so a pathological/huge message can't blow up the message list layout.
function truncateErrorMessage(message: string): string {
  const trimmed = message.trim();
  if (trimmed.length <= ERROR_MESSAGE_MAX_LENGTH) return trimmed;
  return `${trimmed.slice(0, ERROR_MESSAGE_MAX_LENGTH)}…`;
}

// Returns a YYYY-M-D string to detect day boundaries between messages.
function dateKey(createdAt: Date | string | undefined): string {
  if (!createdAt) return "";
  const d = createdAt instanceof Date ? createdAt : new Date(createdAt);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

// Returns a human-readable day label: "Today", "Yesterday", or a formatted date.
function formatDateLabel(
  createdAt: Date | string | undefined,
  t: (key: TranslationKey) => string,
): string {
  if (!createdAt) return "";
  const d = createdAt instanceof Date ? createdAt : new Date(createdAt);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const todayKey = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = `${yesterday.getFullYear()}-${yesterday.getMonth() + 1}-${yesterday.getDate()}`;
  const msgKey = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
  if (msgKey === todayKey) return t("date.today");
  if (msgKey === yesterdayKey) return t("date.yesterday");
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

export function MessageList({ messages, messageMetrics = new Map(), isLoading, error, companionName, onSpeak, speakingMessageId, seekTo, getPlaybackProgress, audioCache, voiceId, playbackSpeed, onSpeedChange, onContainerRef, reactions, onReact, isKeepsaked, onKeepsake, scrollToMessageId, onScrollToComplete, onSendMessage, isReplaying = false, showReasoning, turnPhase, turnStartedAt, subprocessAlive = true, sessionId }: MessageListProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoScroll = useRef(true);
  const { t } = useI18n();
  // Track the previous isReplaying value to detect the true→false transition.
  const wasReplayingRef = useRef(isReplaying);

  // Skip scroll events caused by programmatic scrolling to avoid jitter
  const isAutoScrolling = useRef(false);

  function handleScroll() {
    if (isAutoScrolling.current) return;
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldAutoScroll.current = distanceFromBottom < 120;
  }

  useEffect(() => {
    const justSettled = wasReplayingRef.current && !isReplaying;
    wasReplayingRef.current = isReplaying;

    if (isReplaying) {
      // While replaying: skip the per-message scroll entirely.
      // The container stays wherever it is (top) — no crawl.
      return;
    }

    if (justSettled) {
      // Replay just finished — snap to bottom exactly once.
      // Force shouldAutoScroll true: the user hasn't manually scrolled yet.
      shouldAutoScroll.current = true;
    }

    if (shouldAutoScroll.current && containerRef.current) {
      isAutoScrolling.current = true;
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      requestAnimationFrame(() => { isAutoScrolling.current = false; });
    }
  }, [messages, isLoading, error, isReplaying]);

  // Stable ref for the scroll-complete callback — avoids re-arming the effect on re-renders
  const scrollCompleteRef = useRef(onScrollToComplete);
  scrollCompleteRef.current = onScrollToComplete;

  // Scroll to a specific message (e.g. keepsake click from the sidebar/gallery
  // deep-linking into a DIFFERENT, possibly-not-yet-loaded session).
  //
  // Bug fix (2026-07-15, keepsakes "hit or miss" deep-link): this used to
  // depend on ONLY [scrollToMessageId] — a fixed ~10 x 200ms retry window
  // that started the instant scrollToMessageId was set, regardless of
  // whether the target session had actually finished loading yet. A cold
  // session switch (WS replay from disk) can easily take longer than ~2s,
  // so the retry loop gave up before the message ever entered the DOM, and
  // once `messages` finally updated nothing re-armed the search — a
  // permanent miss. Fix: also depend on `messages`, so every time the
  // messages array changes (including "session finished loading, here's
  // its full history") the effect re-runs and gets a fresh retry budget.
  // This is "load the target session THEN scroll", not "scroll and hope
  // the load finishes in time".
  useEffect(() => {
    if (!scrollToMessageId || !containerRef.current) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;
    const tryScroll = () => {
      if (cancelled) return;
      const el = containerRef.current?.querySelector(`[data-message-id="${CSS.escape(scrollToMessageId)}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("msg-highlight");
        setTimeout(() => el.classList.remove("msg-highlight"), 2000);
        scrollCompleteRef.current?.();
        return;
      }
      attempts++;
      if (attempts < maxAttempts) timer = setTimeout(tryScroll, 200);
    };
    let timer = setTimeout(tryScroll, 100);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [scrollToMessageId, messages]);

  // Compute which user messages have been "read" (i.e. Rook replied after them).
  // We find the index of each user message and check if any assistant message
  // appears later in the array. Done in O(n) with a backwards pass.
  const readUserMessageIds = new Set<string>();
  let seenAssistantAfter = false;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role === "assistant") {
      seenAssistantAfter = true;
    } else if (m.role === "user" && seenAssistantAfter) {
      readUserMessageIds.add(m.id);
    }
  }

  // Build render list, injecting day-boundary separators only.
  // Per-bubble timestamps are rendered inside ChatMessage (.msg-timestamp).
  // The group-timestamp element (minute-level "14:23" header) has been removed —
  // it duplicated the date information already visible on each bubble.
  const renderedItems: JSX.Element[] = [];
  let lastDate = "";

  for (const msg of messages) {
    const msgDate = dateKey(msg.createdAt);
    if (msgDate && msgDate !== lastDate) {
      lastDate = msgDate;
      const dateLabel = formatDateLabel(msg.createdAt, t);
      if (dateLabel) {
        renderedItems.push(
          <div key={`date-${msgDate}`} className="date-separator">
            <span className="date-separator-label">{dateLabel}</span>
          </div>
        );
      }
    }

    renderedItems.push(
      <ChatMessage
        key={msg.id}
        message={msg}
        companionName={companionName}
        isRead={readUserMessageIds.has(msg.id)}
        onSpeak={onSpeak}
        speakingMessageId={speakingMessageId}
        audioCache={audioCache}
        voiceId={voiceId}
        onSeek={seekTo}
        getPlaybackProgress={getPlaybackProgress}
        playbackSpeed={playbackSpeed}
        onSpeedChange={onSpeedChange}
        reactions={reactions?.[msg.id]}
        onReact={onReact}
        isKeepsaked={isKeepsaked?.(msg.id)}
        onKeepsake={onKeepsake}
        metrics={messageMetrics.get(msg.id)}
        onSendMessage={onSendMessage}
        showReasoning={showReasoning}
        sessionId={sessionId}
      />
    );
  }

  const isEmpty = messages.length === 0 && !isLoading;

  function setContainerRef(el: HTMLDivElement | null) {
    containerRef.current = el;
    onContainerRef?.(el);
  }

  return (
    <div className={`chat-messages${isReplaying ? " no-animate" : ""}`} ref={setContainerRef} onScroll={handleScroll} role="log" aria-live="polite" aria-label="Chat messages">
      {isEmpty ? (
        <EmptyState />
      ) : (
        <>
          {renderedItems}
          {turnPhase && turnPhase.phase !== 'streaming' && (
            <ThinkingPulse
              turnPhase={turnPhase}
              turnStartedAt={turnStartedAt ?? null}
              subprocessAlive={subprocessAlive}
            />
          )}
          {error && (
            <div className="message message-error" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <AlertTriangle size={16} />
              {truncateErrorMessage(error.message)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
